export type PivotMetricFormat = "integer" | "decimal" | "currency" | "currency_2" | "currency_int" | "percent" | "percent_2";
export type PivotMetricColor = "margin" | "good_high" | "bad_high";

export interface PivotMetricDefinition {
  key: string;
  label: string;
  group: string;
  format: PivotMetricFormat;
  formula: string;
  color?: PivotMetricColor;
  // Only meaningful when the scope holds several surveys.
  multiSurvey?: boolean;
}

export interface PivotMetricGroup {
  label: string;
  metrics: PivotMetricDefinition[];
}

const group = (label: string, metrics: Omit<PivotMetricDefinition, "group">[]): PivotMetricGroup => ({
  label,
  metrics: metrics.map((m) => ({ ...m, group: label })),
});

export const PIVOT_METRIC_GROUPS: PivotMetricGroup[] = [
  group("General", [
    { key: "surveys", label: "Surveys", format: "integer", formula: "distinct surveys in the row", multiSurvey: true },
    { key: "n", label: "N", format: "integer", formula: "completes_valid" },
    { key: "n_per_survey", label: "N / survey", format: "integer", formula: "N / surveys", multiSurvey: true },
    { key: "cpi", label: "CPI", format: "currency", formula: "cost / N" },
    { key: "margin_pct", label: "Margin %", format: "percent", formula: "(revenue − cost) / revenue", color: "margin" },
    { key: "cpi_client", label: "CPI (cli)", format: "currency", formula: "revenue / N" },
    { key: "margin_eur", label: "Margin €", format: "currency_int", formula: "revenue − cost" },
    { key: "cost", label: "Cost", format: "currency_int", formula: "Σ cost" },
  ]),
  group("Perf Questio", [
    { key: "start_rate", label: "Start rate", format: "percent", formula: "started / sc" },
    { key: "compl_rate", label: "Overall Compl.", format: "percent", formula: "completes_valid / started", color: "good_high" },
    { key: "so_rate", label: "Share of SO", format: "percent", formula: "screenouts / started", color: "bad_high" },
    { key: "soq_rate", label: "Share of SO Qual", format: "percent", formula: "quality screenouts / started", color: "bad_high" },
    { key: "qf_rate", label: "Share of QF", format: "percent", formula: "quota fulls / started", color: "bad_high" },
    { key: "drop_rate", label: "Share of Drop", format: "percent", formula: "dropouts / started", color: "bad_high" },
    { key: "no_target_rate", label: "Share of No Tgt", format: "percent", formula: "no target / started", color: "bad_high" },
  ]),
  group("Perf Ads", [
    { key: "cpm", label: "CPM", format: "currency", formula: "cost / impressions × 1000" },
    { key: "ctr", label: "CTR", format: "percent_2", formula: "clicks / impressions" },
    { key: "cpc", label: "CPC", format: "currency_2", formula: "cost / clicks" },
  ]),
  group("Raw data", [
    { key: "raw_sc", label: "SC (total)", format: "integer", formula: "survey_customers total" },
    { key: "raw_started", label: "Started", format: "integer", formula: "started = true" },
    { key: "raw_completes_valid", label: "Completes valid", format: "integer", formula: "completes_valid" },
    { key: "raw_quota_fulls", label: "Quota Fulls", format: "integer", formula: "quota fulls" },
    { key: "raw_screenouts", label: "Screenouts", format: "integer", formula: "screenouts" },
    { key: "raw_quality_screenouts", label: "Quality SOs", format: "integer", formula: "quality screenouts" },
    { key: "raw_dropouts", label: "Dropouts", format: "integer", formula: "dropouts" },
    { key: "raw_no_targets", label: "No Target", format: "integer", formula: "no target" },
    { key: "raw_impressions", label: "Impressions", format: "integer", formula: "Σ impressions" },
    { key: "raw_clicks", label: "Clicks", format: "integer", formula: "Σ clicks" },
    { key: "raw_revenue", label: "Revenue", format: "currency_int", formula: "Σ cpi paid" },
  ]),
];

export const PIVOT_METRICS: PivotMetricDefinition[] = PIVOT_METRIC_GROUPS.flatMap((g) => g.metrics);

export const DEFAULT_METRICS = [
  "surveys", "n", "n_per_survey", "cpi", "margin_pct", "cpi_client", "cost",
  "start_rate", "compl_rate", "so_rate", "drop_rate", "cpm", "ctr", "cpc",
];

export function formatMetricValue(metric: PivotMetricDefinition, value: number | null | undefined): string {
  if (value == null) return "—";
  switch (metric.format) {
    case "integer":
      return Math.round(value).toLocaleString();
    case "decimal":
      return value.toFixed(1);
    case "currency":
      return `€${value.toFixed(1)}`;
    case "currency_2":
      return `€${value.toFixed(2)}`;
    case "currency_int":
      return `€${Math.round(value).toLocaleString()}`;
    case "percent":
      return `${Math.round(value * 100)}%`;
    case "percent_2":
      return `${(value * 100).toFixed(2)}%`;
  }
}

// Axis ticks: same unit as the values, without the decimals a rounded scale does not need.
export function formatMetricTick(metric: PivotMetricDefinition, value: number): string {
  const short = Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1);
  switch (metric.format) {
    case "currency":
    case "currency_2":
    case "currency_int":
      return `€${short}`;
    case "percent":
    case "percent_2":
      return `${Math.round(value * 100)}%`;
    default:
      return short;
  }
}

export function metricCellClass(metric: PivotMetricDefinition, value: number | null | undefined): string {
  if (value == null || !metric.color) return "";
  switch (metric.color) {
    case "margin":
      if (value >= 0.6) return "text-green-600 font-medium";
      if (value < 0.4) return "text-red-600 font-medium";
      return "";
    case "good_high":
      if (value >= 0.3) return "text-green-600 font-medium";
      if (value >= 0.2) return "text-green-500";
      return "";
    case "bad_high":
      if (value >= 0.2) return "text-red-600 font-medium";
      if (value >= 0.15) return "text-orange-500";
      return "";
  }
}
